import { Button, EmptyListContent } from "@jugl-web/ui-kit";
import React, { useMemo } from "react";
import { CRMChannel } from "@jugl-web/rest-api";
import { useTranslations } from "@jugl-web/utils";
import { useMe } from "@web-src/modules/auth/hooks/useMe";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { useWhatsappAccount } from "../../../../providers/WhatsappAccountProvider/useWhatsappAccount";
import { useInstagramAccount } from "../../../../providers/InstagramAccountProvider/useInstagramAccount";
import { useWebChatBotAccount } from "../../../../providers/WebChatBotAccountProvider/useWebChatBotAccount";
import { useEmailAccount } from "../../../../providers/EmailAccountProvider/useEmailAccount";
import { ReactComponent as InstagramIcon } from "./assets/instagram.svg";
import { ReactComponent as WhatsappIcon } from "./assets/whatsapp.svg";
import { ReactComponent as WebChatBotIcon } from "./assets/web-chat-bot.svg";
import { ReactComponent as TwilioIcon } from "./assets/twilio.svg";
import { ReactComponent as PhoneCallsIcon } from "./assets/voice.svg";
import { ReactComponent as EmailIcon } from "./assets/email.svg";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import { useManageAccountsDrawer } from "../../../ManageAccountsDrawer/providers/ManageAccountsDrawerContext";

export const SelectChatSection: React.FC = () => {
  const { t } = useTranslations();
  const { selectedChannel } = useClientSupportPageContext();
  const { crmConfig, openManageAccountDrawer } = useManageAccountsDrawer();
  const { whatsappAgent, reconnectWhatsappAccount, isRegisteringAccount } =
    useWhatsappAccount();
  const { instagramAccount, loginInstagramAccount } = useInstagramAccount();
  const {
    webChatBotAccount,
    toggleWebChatBotAccount,
    isToggleWebChatBotAccountLoading,
  } = useWebChatBotAccount();
  const { isConnected: isEmailConnected } = useEmailAccount();
  const { me, profile } = useMe();

  const { title, subtitle, isConnected, img, disconnectedButton } =
    useMemo(() => {
      const connectedTitle = t(
        {
          id: "client-support-page.welcome-back-name",
          defaultMessage: "Welcome back, {name}!",
        },
        { name: profile?.firstName || me?.profile?.first_name || "" }
      );

      const connectedSubtitle = t({
        id: "client-support-page.ai-standing-by-open-conversation",
        defaultMessage:
          "AI is standing by. Open a conversation to get started.",
      });

      const config = {
        [CRMChannel.whatsapp]: {
          isConnected: whatsappAgent?.phone_number.status === "CONNECTED",
          icons: <WhatsappIcon />,
          disconnectedTitle: t({
            id: "client-support-page.whatsapp-account-is-not-connected",
            defaultMessage: "WhatsApp Account is not connected",
          }),
          disconnectedSubtitle: t({
            id: "client-support-page.reconnect-to-continue-using-whatsapp-crm",
            defaultMessage: "Please reconnect to continue using WhatsApp CRM",
          }),
          disconnectedButton: {
            text: t({
              id: "common.reconnect",
              defaultMessage: "Reconnect",
            }),
            onClick: reconnectWhatsappAccount,
            isDisabled: isRegisteringAccount,
          },
        },
        [CRMChannel.instagram]: {
          isConnected: instagramAccount?.status === "connected",
          icons: <InstagramIcon />,
          disconnectedTitle: t({
            id: "client-support-page.instagram-account-is-not-connected",
            defaultMessage: "Instagram Account is not connected",
          }),
          disconnectedSubtitle: t({
            id: "client-support-page.reconnect-to-continue-using-instagram-crm",
            defaultMessage: "Please reconnect to continue using Instagram CRM",
          }),
          disconnectedButton: {
            text: t({ id: "common.reconnect", defaultMessage: "Reconnect" }),
            onClick: loginInstagramAccount,
          },
        },
        [CRMChannel.chatbot]: {
          isConnected: webChatBotAccount?.enabled,
          icons: <WebChatBotIcon />,
          disconnectedTitle: t({
            id: "client-support-page.web-chat-is-disabled",
            defaultMessage: "Web Chat is disabled",
          }),
          disconnectedSubtitle: t({
            id: "client-support-page.enable-to-continue-using-web-chat-crm",
            defaultMessage: "Please enable it to continue using Web Chat CRM",
          }),
          disconnectedButton: {
            text: t({ id: "common.enable", defaultMessage: "Enable" }),
            onClick: toggleWebChatBotAccount,
            isDisabled: isToggleWebChatBotAccountLoading,
          },
        },
        [CRMChannel.twilio]: {
          isConnected: !!crmConfig?.twilio?.id,
          icons: <TwilioIcon />,
          disconnectedTitle: t({
            id: "client-support-page.twilio-is-disabled",
            defaultMessage: "Text (SMS) is disabled",
          }),
          disconnectedSubtitle: t({
            id: "client-support-page.enable-to-continue-using-twilio-crm",
            defaultMessage: "Please enable it to continue using Text (SMS) CRM",
          }),
          disconnectedButton: {
            text: t({ id: "common.reconnect", defaultMessage: "Reconnect" }),
            onClick: () => openManageAccountDrawer({ name: "twilioAccount" }),
          },
        },
        [CRMChannel.voice]: {
          isConnected: !!crmConfig?.voice?.id,
          icons: <PhoneCallsIcon />,
          disconnectedTitle: t({
            id: "client-support-page.voice-is-disabled",
            defaultMessage: "Phone calls is disabled",
          }),
          disconnectedSubtitle: t({
            id: "client-support-page.enable-to-continue-using-voice",
            defaultMessage:
              "Please enable it to continue using Phone calls CRM",
          }),
          disconnectedButton: undefined,
        },
        [CRMChannel.email]: {
          isConnected: isEmailConnected,
          icons: <EmailIcon />,
          disconnectedTitle: t({
            id: "client-support-page.email-account-is-not-connected",
            defaultMessage: "Email Account is not connected",
          }),
          disconnectedSubtitle: t({
            id: "client-support-page.reconnect-to-continue-using-email-inbox",
            defaultMessage:
              "Please reconnect to continue receiving customer Emails",
          }),
          disconnectedButton: {
            text: t({ id: "common.reconnect", defaultMessage: "Reconnect" }),
            onClick: () => openManageAccountDrawer({ name: "emailSetup" }),
          },
        },
      };

      const channelConfig = config[selectedChannel];
      if (!channelConfig) {
        throw new Error(`Unsupported crm channel type: ${selectedChannel}`);
      }

      return {
        title: channelConfig.isConnected
          ? connectedTitle
          : channelConfig.disconnectedTitle,
        subtitle: channelConfig.isConnected
          ? connectedSubtitle
          : channelConfig.disconnectedSubtitle,
        img: channelConfig.icons,
        isConnected: channelConfig.isConnected,
        disconnectedButton: channelConfig.disconnectedButton,
      };
    }, [
      t,
      profile?.firstName,
      me?.profile?.first_name,
      whatsappAgent,
      reconnectWhatsappAccount,
      isRegisteringAccount,
      instagramAccount?.status,
      loginInstagramAccount,
      webChatBotAccount?.enabled,
      toggleWebChatBotAccount,
      isToggleWebChatBotAccountLoading,
      crmConfig?.twilio?.id,
      crmConfig?.voice?.id,
      isEmailConnected,
      openManageAccountDrawer,
      selectedChannel,
    ]);

  return (
    <div className="h-full w-full bg-grey-100">
      <EmptyListContent
        className="w-[500px] gap-2"
        type="custom"
        customTitle={title}
        customSubtitle={subtitle}
        customImg={
          <div className="mb-6 drop-shadow-[-12px_12px_15px_rgba(87,68,111,0.16)]">
            {img}
          </div>
        }
        customButtonSlot={
          !isConnected && disconnectedButton ? (
            <Button
              variant="primary"
              size="sm"
              className="mt-2"
              iconEnd={<ArrowRightIcon />}
              onClick={disconnectedButton.onClick}
              isDisabled={
                "isDisabled" in disconnectedButton
                  ? disconnectedButton.isDisabled
                  : false
              }
            >
              {disconnectedButton.text}
            </Button>
          ) : undefined
        }
      />
    </div>
  );
};
